import { useCallback } from "react"

import { useReactFlow, useStoreApi } from "reactflow"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"

import type { UpdateGraphParams } from "../types/interfaces"
import { handleDeletingNodeInBetween } from "../utils/workflowHelpers"

/**
 * Hook for handling node deletion logic.
 * Provides consistent deletion behavior across all node types.
 * Reduces ~50 lines per node component.
 */
export function useNodeDeletion(
  nodeId: string,
  updateGraph?: (data: UpdateGraphParams) => void,
): { handleRemoveNode: (successMessage?: string) => void } {
  const { getEdges } = useReactFlow()
  const { getNodes } = useStoreApi().getState()

  const handleRemoveNode = useCallback(
    (successMessage?: string) => {
      const deleted = getNodes().filter((node) => node.id === nodeId)
      const newNodesAndEdges = handleDeletingNodeInBetween(deleted, getEdges(), getNodes(), nodeId)

      if (updateGraph) {
        updateGraph({ dataAfterDeletion: { nodes: newNodesAndEdges?.nodes, edges: newNodesAndEdges?.edges } })
      }

      if (successMessage) {
        NotificationUtils.toast(successMessage, { severity: "success" })
      }
    },
    [nodeId, updateGraph, getEdges, getNodes],
  )

  return { handleRemoveNode }
}

/**
 * Hook for managing node data updates.
 * Provides consistent data update patterns across all node types.
 * Reduces ~100 lines per node component.
 */
export function useNodeData(nodeId: string): {
  updateNodeData: (data: Record<string, unknown>) => void
  getCurrentNodeData: () => unknown
} {
  const { setNodes, getNodes } = useStoreApi().getState()

  const updateNodeData = useCallback(
    (data: Record<string, unknown>) => {
      setNodes(
        getNodes().map((node) => {
          const newNode = { ...node }
          if (node.id === nodeId) {
            newNode.data = {
              ...newNode.data,
              ...data,
            }
          }
          return newNode
        }),
      )
    },
    [nodeId, setNodes, getNodes],
  )

  const getCurrentNodeData = useCallback(() => {
    return getNodes().find((node) => node.id === nodeId)?.data
  }, [nodeId, getNodes])

  return { updateNodeData, getCurrentNodeData }
}

/**
 * Hook for managing node handles positioning.
 * Provides consistent handle management across all node types.
 * Reduces ~30 lines per node component.
 */
export function useNodeHandles(
  nodeId: string,
  _menuView: boolean,
): {
  getNodeWidth: () => number
  getSourceHandles: (handleCount: number) => Array<{ id: string; position: number }>
} {
  const { getNodes } = useStoreApi().getState()

  const getNodeWidth = useCallback(() => {
    return getNodes().find((node) => node.id === nodeId)?.width || 300
  }, [nodeId, getNodes])

  const getSourceHandles = useCallback(
    (handleCount: number) => {
      if (handleCount <= 1) return []

      const nodeWidth = getNodeWidth()
      return Array.from({ length: handleCount }, (_, index) => ({
        id: index === 0 ? "a" : "b",
        position: (nodeWidth / (handleCount + 1)) * (index + 1),
      }))
    },
    [getNodeWidth],
  )

  return { getNodeWidth, getSourceHandles }
}
